import { clsx } from 'clsx';
import {
  forwardRef,
  type InputHTMLAttributes,
  type ReactNode,
  useId,
} from 'react';

import { Label } from './label';

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: ReactNode;
  labelClassName?: string;
  error?: string;
  helperText?: string;
  fullWidth?: boolean;
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      label,
      labelClassName,
      error,
      helperText,
      fullWidth = false,
      startIcon,
      endIcon,
      id,
      required,
      ...props
    },
    ref
  ) => {
    const generatedId = useId();
    const inputId = id ?? generatedId;

    const baseClasses =
      'block p-3 border rounded-lg placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-0 sm:text-sm transition-colors';

    const stateClasses = error
      ? 'border-red-300 text-red-900 focus:ring-red-500 focus:border-red-500'
      : 'border-[#1C1B181F] focus:ring-primary focus:border-primary';

    const classes = clsx(
      baseClasses,
      stateClasses,
      fullWidth && 'w-full',
      startIcon && 'pl-10',
      endIcon && 'pr-10',
      className
    );

    return (
      <div className={clsx('relative', fullWidth && 'w-full')}>
        {label && (
          <Label
            htmlFor={inputId}
            isRequired={required}
            className={clsx('mb-1.5 flex items-center gap-1', labelClassName)}
          >
            {label}
          </Label>
        )}

        <div className="relative">
          {startIcon && (
            <div className="absolute inset-y-0 left-0 flex items-center pl-3">
              <div className="z-10 size-5 cursor-pointer">{startIcon}</div>
            </div>
          )}

          <input
            ref={ref}
            id={inputId}
            required={required}
            className={classes}
            {...props}
          />

          {endIcon && (
            <div className="absolute inset-y-0 right-0 flex items-center pr-3">
              <div className="z-10 cursor-pointer">{endIcon}</div>
            </div>
          )}
        </div>

        {error && <p className="mt-1 text-sm text-red-600">{error}</p>}

        {helperText && !error && (
          <p className="mt-1 text-sm text-gray-500">{helperText}</p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';

export { Input };
